import { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { SidebarInset, SidebarProvider, SIDEBAR_WIDTH_ICON_PX } from '@/components/ui/sidebar';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { SuperAdminDebugOverlay } from '@/components/debug/SuperAdminDebugOverlay';
import { DebugClipboardButton } from '@/components/debug/DebugClipboardButton';
import { SuperAdminHintsControl } from '@/components/debug/SuperAdminHintsButton';
import { OrgMFAEnforcementScreen } from '@/components/auth/OrgMFAEnforcementScreen';
import { MFAVerification } from '@/components/auth/MFAVerification';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { AdminSidebar } from './AdminSidebar';
import { useOrganization } from '@/contexts/OrganizationContext';
import {
  clearPortalSwitchIntent,
  readPortalSwitchIntent,
  subscribeToPortalSwitchIntent,
} from '@/lib/portalSwitch';
import { ADMIN_PROFILE_LABEL } from '@/lib/surfaceViewLabel';
import { SurfaceSidebarContextToggleButton } from './SurfaceSidebar';
import {
  SupportNoticeBanner,
  useActiveSupportNoticeBannerState,
} from './SupportNoticeBanner';
import { isMfaRequired, isPreviewMfaBypass } from '@/lib/auth';
import { useAalStepUpGate } from '@/hooks/useAalStepUpGate';
import { buildAuthRedirectPath } from '@/lib/authReturnTo';

interface AdminLayoutProps {
  children?: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useOrganization();
  const [portalSwitchIntent, setPortalSwitchIntent] = useState(() => readPortalSwitchIntent());
  // ENT-1253: super_admin reachable on /admin must be gated on a verified
  // MFA factor. AAL2 enforcement on sensitive admin actions is only
  // meaningful if every actor reachable through this surface has a factor
  // on file. Mirrors the AppLayout pattern (ENT-1140) so a factor-less
  // super admin signing into Auth.tsx and being routed to /admin via
  // currentViewMode lands on <OrgMFAEnforcementScreen> instead of the
  // admin shell.
  const [hasMFA, setHasMFA] = useState(false);
  const [mfaCheckComplete, setMfaCheckComplete] = useState(false);
  const lastMfaCheckUserRef = useRef<string | null>(null);
  const {
    visibleNotices: visibleSupportNotices,
    isLoading: isSupportNoticeLoading,
    errorMessage: supportNoticeErrorMessage,
  } = useActiveSupportNoticeBannerState();
  const shouldHideSupportNotices = portalSwitchIntent !== null;

  useEffect(() => subscribeToPortalSwitchIntent(setPortalSwitchIntent), []);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!user) {
      // ENT-1958: preserve the deep-link destination through sign-in + MFA.
      navigate(buildAuthRedirectPath(location));
      return;
    }

    localStorage.setItem('currentViewMode', 'admin');
  }, [isLoading, navigate, user, location]);

  useEffect(() => {
    if (portalSwitchIntent?.target === 'admin') {
      clearPortalSwitchIntent('admin');
    }
  }, [portalSwitchIntent?.target]);

  useEffect(() => {
    let isActive = true;
    const userIdAtStart = user?.id ?? null;

    if (!user) {
      lastMfaCheckUserRef.current = null;
      setHasMFA(false);
      setMfaCheckComplete(false);
      return;
    }

    const mfaCheckUserKey = `${user.id}:${user.email ?? ''}`;

    if (lastMfaCheckUserRef.current === mfaCheckUserKey) {
      return;
    }

    lastMfaCheckUserRef.current = mfaCheckUserKey;
    setMfaCheckComplete(false);

    const checkFactors = async () => {
      try {
        const enforced = isMfaRequired(user.email);
        if (!isActive || user.id !== userIdAtStart) {
          return;
        }
        if (!enforced) {
          // MFA not enforced for this admin/env (preview, or an allowlisted
          // email): the enrollment screen is skipped. The *session* AAL gate
          // is handled independently below (it runs its own probe), so an
          // allowlisted-yet-enrolled admin is still challenged.
          setHasMFA(true);
          return;
        }

        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        if (!isActive || user.id !== userIdAtStart) {
          return;
        }
        const verifiedFactor = factorsData?.totp?.some((factor) => factor.status === 'verified') ?? false;
        setHasMFA(verifiedFactor);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        console.error('Error checking admin MFA requirement:', error);
        if (!isActive || user.id !== userIdAtStart) {
          return;
        }
        // Fail closed: treat the lookup failure as "no factor" so the
        // enrolment screen renders instead of the admin shell.
        setHasMFA(false);
      } finally {
        if (isActive && user.id === userIdAtStart) {
          setMfaCheckComplete(true);
        }
      }
    };

    void checkFactors();

    return () => {
      isActive = false;
    };
  }, [user?.id, user?.email]);

  const handleMFAEnabled = () => {
    setHasMFA(true);
  };

  const handleReturnToSignIn = async () => {
    const { signOutLocally } = await import('@/lib/auth');
    await signOutLocally('/auth');
  };

  // ENT-2770: an enrolled admin whose *session* is still aal1 must complete a
  // step-up challenge before reaching the admin shell. Enable signal is
  // exactly `!isPreviewMfaBypass()` — the same carve-out sign-in uses to
  // decide whether to challenge. NOT `hasMFA && mfaEnforced`: `mfaEnforced`
  // (= `isMfaRequired(email)`) honours the per-email allowlist, but sign-in
  // challenges allowlisted-yet-enrolled admins anyway (Codex ENT-2770 P2), and
  // a `listFactors` failure would leave `hasMFA` false and disable the gate.
  // The hook runs its own enrollment probe and passes non-enrolled admins
  // through as `satisfied`, so this is safe for admins without a factor.
  const stepUpGate = useAalStepUpGate(mfaCheckComplete && !isPreviewMfaBypass());

  if (!user) {
    // ENT-1958: while signed out, hold the loading state and let the effect
    // above redirect to /auth?returnTo=… . This layout owns that redirect: the
    // admin page defers to it (ENT-5492), and children are not rendered so no
    // page-level redirect can win the race and drop the returnTo.
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <p className="text-sm text-muted-foreground">Loading your admin workspace…</p>
      </div>
    );
  }

  if (user && !mfaCheckComplete) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <p className="text-sm text-muted-foreground">Loading your admin workspace…</p>
      </div>
    );
  }

  if (user && mfaCheckComplete && !hasMFA) {
    return (
      <OrgMFAEnforcementScreen
        organizationName="Platform Admin"
        onMFAEnabled={handleMFAEnabled}
      />
    );
  }

  // ENT-2770: enrolled but the session is aal1 — challenge before the shell.
  if (stepUpGate.status === 'step-up-required' && stepUpGate.factorId) {
    return (
      <MFAVerification
        factorId={stepUpGate.factorId}
        onSuccess={stepUpGate.recheck}
        onCancel={handleReturnToSignIn}
      />
    );
  }

  if (stepUpGate.status === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <p className="text-sm text-muted-foreground">Verifying your two-factor session…</p>
      </div>
    );
  }

  if (stepUpGate.status === 'unavailable') {
    // Fail closed: the assurance probe could not confirm an aal2 session.
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <p className="max-w-sm text-sm text-muted-foreground">
          We couldn't verify your two-factor session. Retry, or return to sign in and try again.
        </p>
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={stepUpGate.recheck}>
            Retry
          </Button>
          <Button type="button" onClick={() => { void handleReturnToSignIn(); }}>
            Return to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider condensedInsetPx={SIDEBAR_WIDTH_ICON_PX}>
      <AdminSidebar />
      <SidebarInset className="h-svh overflow-hidden">
        <SupportNoticeBanner
          notices={shouldHideSupportNotices ? [] : visibleSupportNotices}
          isLoading={isSupportNoticeLoading || shouldHideSupportNotices}
          errorMessage={supportNoticeErrorMessage}
        />
        <header
          className="sticky top-0 z-40 flex h-14 shrink-0 items-center justify-between border-b border-border/60 bg-background/95 px-4 backdrop-blur"
          data-testid="admin-shell-header"
        >
          <div className="flex items-center gap-2">
            <SurfaceSidebarContextToggleButton className="-ml-1 h-7 w-7 rounded-md hover:bg-black/5 dark:hover:bg-white/10" />
            <div>
              <p className="text-sm font-semibold">{ADMIN_PROFILE_LABEL}</p>
              <p className="text-xs text-muted-foreground">Separate platform-admin surface</p>
            </div>
          </div>
          <div data-testid="admin-shell-header-actions" className="flex items-center gap-2">
            <SuperAdminDebugOverlay />
            <NotificationBell />
          </div>
        </header>
        <div className="platform-view-pane flex-1 overflow-hidden">
          <main
            className="relative z-10 h-full overflow-y-auto"
            role="main"
          >
            {children ?? <Outlet />}
          </main>
        </div>
        <DebugClipboardButton companionWidgetIds={[]} />
        <SuperAdminHintsControl />
      </SidebarInset>
    </SidebarProvider>
  );
}
